import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Plus, QrCode, Link2, Copy, Trash2, User } from "lucide-react";

export default function AgentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState(null);
  const [form, setForm] = useState({ name: "", surname: "", agent_code: "", phone: "", email: "", area: "", active: true });

  const load = useCallback(() => api.get("/admin/agents").then((r) => setAgents(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.surname || !form.agent_code) { toast.error("Nome, cognome e codice sono obbligatori"); return; }
    try {
      await api.post("/admin/agents", form);
      toast.success("Agente creato");
      setOpen(false);
      setForm({ name: "", surname: "", agent_code: "", phone: "", email: "", area: "", active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo agente?")) return;
    await api.delete(`/admin/agents/${id}`);
    toast.success("Agente eliminato");
    load();
  };

  const showQr = async (id) => {
    const r = await api.get(`/admin/agents/${id}/qr`, { params: { base_url: window.location.origin } });
    setQr(r.data);
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`${window.location.origin}/a/${code}`);
    toast.success("Link copiato");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Agenti</h1>
          <p className="text-gray-500 text-sm mt-1">Gestione agenti myPOS e link personali</p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-agent-btn" className="rounded-xl bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nuovo agente</Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Nuovo agente</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <Field label="Nome *"><Input data-testid="agent-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Cognome *"><Input data-testid="agent-form-surname" value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} /></Field>
                <Field label="Codice agente *"><Input data-testid="agent-form-code" value={form.agent_code} onChange={(e) => setForm({ ...form, agent_code: e.target.value.toUpperCase() })} placeholder="es. LUCA001" /></Field>
                <Field label="Area / Agenzia"><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></Field>
                <Field label="Telefono"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              </div>
              <DialogFooter><Button onClick={create} data-testid="agent-form-submit" className="rounded-xl bg-[#111111] text-white">Crea agente</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {agents.length === 0 && <p className="text-gray-400 text-sm">Nessun agente registrato.</p>}
        {agents.map((a) => (
          <div key={a.id} data-testid={`agent-card-${a.agent_code}`} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#FFFBEB] flex items-center justify-center"><User className="w-5 h-5 text-[#E6B800]" /></div>
                <div>
                  <p className="font-semibold">{a.name} {a.surname}</p>
                  <p className="text-xs text-gray-500 font-mono">{a.agent_code}</p>
                </div>
              </div>
              <Badge className={a.active ? "bg-green-100 text-green-700 border-0" : "bg-gray-100 text-gray-500 border-0"}>{a.active ? "Attivo" : "Sospeso"}</Badge>
            </div>
            {a.area && <p className="text-sm text-gray-500 mt-3">{a.area}</p>}
            <div className="grid grid-cols-3 gap-2 mt-4 text-center">
              <Stat label="Segnalazioni" value={a.reports_count} />
              <Stat label="Attivazioni" value={a.activations_count} />
              <Stat label="Conversione" value={`${a.conversion}%`} />
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => copyLink(a.agent_code)} data-testid={`copy-link-${a.agent_code}`}><Copy className="w-3.5 h-3.5 mr-1" /> Link</Button>
              <Button size="sm" variant="outline" className="rounded-lg flex-1" onClick={() => showQr(a.id)} data-testid={`qr-btn-${a.agent_code}`}><QrCode className="w-3.5 h-3.5 mr-1" /> QR</Button>
              {isAdmin && <Button size="sm" variant="outline" className="rounded-lg text-red-500 hover:text-red-600" onClick={() => remove(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!qr} onOpenChange={() => setQr(null)}>
        <DialogContent className="rounded-2xl text-center">
          <DialogHeader><DialogTitle>QR code personale</DialogTitle></DialogHeader>
          {qr && (
            <div className="flex flex-col items-center gap-4 py-2">
              <img src={qr.qr} alt="QR" className="w-56 h-56" data-testid="qr-image" />
              <div className="flex items-center gap-2 text-sm bg-gray-100 rounded-lg px-3 py-2 w-full justify-center">
                <Link2 className="w-4 h-4 text-gray-400" /><span className="truncate">{qr.link}</span>
              </div>
              <Button onClick={() => { navigator.clipboard.writeText(qr.link); toast.success("Link copiato"); }} className="rounded-xl bg-[#111111] text-white w-full">Copia link</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) { return <div><Label className="mb-1.5 block text-xs text-gray-500">{label}</Label>{children}</div>; }
function Stat({ label, value }) { return <div className="bg-gray-50 rounded-lg py-2"><p className="font-display font-bold">{value}</p><p className="text-[10px] text-gray-500">{label}</p></div>; }
